/**
 * Brand logos tab model (pure — unit tested): which bucket a row falls in, the
 * search, filter counts and sort order, what "Find logos for missing" asks
 * for, which row a typed brand name already is, the initials badge, and the
 * wording for logo errors.
 *
 * A logo lives on a Sales registry record, and only an `exact` or `alias`
 * match shows it, so a row is "linked" exactly when it has one of those. The
 * buckets partition the list: linked with a logo, linked without one, and
 * not linked (a loose, ambiguous or missing match).
 */

import { fold } from './creatorDashboardSearch';
import type { AdvertiserLogoRow, LogoCandidate } from './types';

export type BrandLogoFilter = 'missing' | 'has' | 'unlinked' | 'all';
export const BRAND_LOGO_FILTERS: BrandLogoFilter[] = ['missing', 'has', 'unlinked', 'all'];
export const BRAND_LOGO_FILTER_LABELS: Record<BrandLogoFilter, string> = {
  missing: 'Missing logo',
  has: 'Has logo',
  unlinked: 'Not in registry',
  all: 'All',
};

/** Mirrors the server caps (advertiserLogosAdmin.ts): find-batch 1–20 names, apply-batch 1–10 picks. */
export const FIND_BATCH_SIZE = 20;
export const APPLY_BATCH_SIZE = 10;

/** Mirrors the upload route: PNG or JPG, at most 5 MB. */
export const LOGO_FILE_ACCEPT = 'image/png,image/jpeg';
export const MAX_LOGO_BYTES = 5 * 1024 * 1024;

/** The registry record whose logo the row shows, or null when no logo can show. */
export const linkedKeyOf = (row: AdvertiserLogoRow): string | null =>
  row.match.state === 'exact' || row.match.state === 'alias' ? row.match.key : null;

/** The registry name a linked row's logo is saved under (the row's own name otherwise). */
export const recordNameOf = (row: AdvertiserLogoRow): string =>
  row.match.state === 'exact' || row.match.state === 'alias' ? row.match.recordName : row.name;

export const bucketOf = (row: AdvertiserLogoRow): Exclude<BrandLogoFilter, 'all'> => {
  if (!linkedKeyOf(row)) return 'unlinked';
  return row.logo ? 'has' : 'missing';
};

function searchText(row: AdvertiserLogoRow): string {
  const parts = [row.name, row.gamAdvertiserId ?? ''];
  const m = row.match;
  if (m.state === 'exact' || m.state === 'alias' || m.state === 'possible') parts.push(m.recordName, m.key);
  else if (m.state === 'ambiguous') parts.push(...m.candidates.map((c) => c.name));
  else if (m.state === 'none' && m.gamIdHint) parts.push(m.gamIdHint.name);
  return fold(parts.join(' '));
}

export interface FilteredBrandLogos {
  rows: AdvertiserLogoRow[];
  /** Per-bucket counts under the current search (for the filter tabs). */
  counts: Record<BrandLogoFilter, number>;
}

/** Every whitespace-separated word must appear (case- and accent-insensitive) in the name or its registry match. */
export function filterBrandLogoRows(
  rows: readonly AdvertiserLogoRow[],
  filters: { q: string; filter: BrandLogoFilter },
): FilteredBrandLogos {
  const terms = fold(filters.q.trim()).split(/\s+/).filter(Boolean);
  const counts: Record<BrandLogoFilter, number> = { missing: 0, has: 0, unlinked: 0, all: 0 };
  const shown: AdvertiserLogoRow[] = [];
  for (const row of rows) {
    if (terms.length > 0) {
      const text = searchText(row);
      if (!terms.every((t) => text.includes(t))) continue;
    }
    const bucket = bucketOf(row);
    counts[bucket] += 1;
    counts.all += 1;
    if (filters.filter === 'all' || filters.filter === bucket) shown.push(row);
  }
  return { rows: shown, counts };
}

/** Brands on reports first, busiest (most network channels) first, then most recent, then by name. */
export function sortBrandLogoRows(rows: readonly AdvertiserLogoRow[]): AdvertiserLogoRow[] {
  return [...rows].sort(
    (a, b) =>
      Number(b.inReports) - Number(a.inReports) ||
      (b.channelCount ?? -1) - (a.channelCount ?? -1) ||
      (b.lastMonth ?? '').localeCompare(a.lastMonth ?? '') ||
      a.name.localeCompare(b.name),
  );
}

/**
 * One find-batch item per registry record still missing a logo, named by its
 * first row in `rows` (pass them sorted, so that is the busiest report name).
 * Records in `skip` (already searched, or a suggestion already staged) are left out.
 */
export function missingLogoTargets(
  rows: readonly AdvertiserLogoRow[],
  skip: ReadonlySet<string>,
): Array<{ name: string; advertiserKey: string }> {
  const seen = new Set<string>();
  const items: Array<{ name: string; advertiserKey: string }> = [];
  for (const row of rows) {
    const key = linkedKeyOf(row);
    if (!key || row.logo || seen.has(key) || skip.has(key)) continue;
    seen.add(key);
    items.push({ name: row.name, advertiserKey: key });
  }
  return items;
}

export function chunk<T>(items: readonly T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size));
  return out;
}

/** The names that share a registry record (and so its logo), in row order. */
export function namesLinkedTo(rows: readonly AdvertiserLogoRow[], key: string): string[] {
  return rows.filter((r) => linkedKeyOf(r) === key).map((r) => r.name);
}

/**
 * The record "Same as…" starts on: a loose name match, or the one record that
 * holds the row's GAM advertiser id.
 */
export function suggestedRecordOf(row: AdvertiserLogoRow): { key: string; name: string; reason: string } | null {
  if (row.match.state === 'possible') return { key: row.match.key, name: row.match.recordName, reason: 'close name match' };
  if (row.match.state === 'none' && row.match.gamIdHint) {
    return { key: row.match.gamIdHint.key, name: row.match.gamIdHint.name, reason: 'same GAM advertiser' };
  }
  return null;
}

/**
 * The listed row a typed brand name already is — a report name, or the record
 * a row is linked to (case- and accent-insensitive) — so "+ Add brand" points
 * at it instead of adding the brand twice. A linked row wins.
 */
export function rowNamed(rows: readonly AdvertiserLogoRow[], name: string): AdvertiserLogoRow | null {
  const q = fold(name.trim());
  if (!q) return null;
  const named = (r: AdvertiserLogoRow) => fold(r.name) === q || (linkedKeyOf(r) !== null && fold(recordNameOf(r)) === q);
  return rows.find((r) => linkedKeyOf(r) !== null && named(r)) ?? rows.find(named) ?? null;
}

/** A record created here, as the registry-only row the next list load holds for it. */
export const registryRowOf = (key: string, name: string): AdvertiserLogoRow => ({
  id: `registry:${key}`,
  name,
  inReports: false,
  channelCount: null,
  lastMonth: null,
  match: { state: 'exact', key, recordName: name, normalizedOnly: false },
  logo: null,
});

/** Badge letters: "Dr. Squatch" → "DS", "Coke (TCCC)" → "CO", "McDonald's" → "MC", "General Mills - REVOLT" → "GM". */
export function initialsOf(name: string): string {
  const words = name
    .replace(/\([^)]*\)/g, ' ')
    .replace(/['’]/g, '')
    .replace(/\s+[-–—]\s+(revolt|ione|urban ?one)\b.*$/i, '')
    .split(/[^A-Za-z0-9\u00C0-\u024F]+/)
    .filter(Boolean);
  if (words.length === 0) return '?';
  const letters = words.length > 1 ? words[0][0] + words[1][0] : words[0].slice(0, 2);
  return letters.toUpperCase();
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "2026-09" → "Sep 2026". */
export function fmtMonth(ym: string): string {
  const [y, m] = ym.split('-').map(Number);
  return MONTHS[m - 1] && y ? `${MONTHS[m - 1]} ${y}` : ym;
}

export const SOURCE_LABELS: Record<LogoCandidate['source'] | 'upload', string> = {
  wikimedia: 'Wikimedia Commons',
  culturegenesis: 'culturegenesis.com',
  upload: 'Uploaded',
};

/**
 * "Wikimedia Commons · CC0" — the source, then the licence unless it already
 * names the source (culturegenesis.com files carry "As used on culturegenesis.com").
 */
export function provenanceOf(source: keyof typeof SOURCE_LABELS, license?: string): string {
  const label = SOURCE_LABELS[source];
  if (!license) return label;
  return license.toLowerCase().includes(label.toLowerCase()) ? license : `${label} · ${license}`;
}

/** Why a file can't be uploaded, checked before it is sent (the server checks the bytes too). */
export function uploadProblem(file: Pick<File, 'name' | 'type' | 'size'>): string | null {
  const typeOk = file.type === 'image/png' || file.type === 'image/jpeg' || (!file.type && /\.(png|jpe?g)$/i.test(file.name));
  if (!typeOk) return 'Use a PNG or JPG file.';
  if (file.size > MAX_LOGO_BYTES) return `That file is ${(file.size / 1024 / 1024).toFixed(1)} MB — the limit is 5 MB.`;
  return null;
}

/**
 * Plain wording for a failed logo write, from an ApiError or an apply-batch
 * result. The server's own message is specific ("That image is blank.", "…at
 * least 64 px across.") and wins, except where it is generic (403), technical
 * (fetch failures carry HTTP statuses) or both "not found" cases at once (the
 * record went away, or its logo was removed elsewhere).
 */
export function logoErrorMessage(err: { status?: number; code?: string; message?: string } | null | undefined): string {
  if (err?.status === 403) return 'You don’t have permission to change brand logos.';
  switch (err?.code) {
    case 'fetch_failed':
      return 'Couldn’t fetch the image from its source — try again, or pick another logo.';
    case 'not_found':
      return 'That advertiser’s registry record changed since the list loaded — refresh the list.';
    case 'unsupported_image':
      return err.message || 'Use a PNG or JPG file.';
    case 'too_small':
      return err.message || 'That image is too small to use as a logo — try a larger file.';
    default:
      return err?.message || 'Something went wrong.';
  }
}

/** "Culture Genesis - Brand logos - 2026-10-20.csv" (local date). */
export function logoCsvFileName(now: Date = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `Culture Genesis - Brand logos - ${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}.csv`;
}
